import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import i18n from './i18n';

export const UserContext = createContext(null);

const USER_SESSION_HINT_KEY = 'tenryu_user_session_hint';
const ADMIN_SESSION_HINT_KEY = 'tenryu_admin_session_hint';
const MEMBER_COOKIE_NAME = 'tenryu_member';
const MEMBER_COOKIE_MAX_AGE_YEARS = 10;

function setUserSessionHint(on) {
  try {
    if (on) localStorage.setItem(USER_SESSION_HINT_KEY, '1');
    else localStorage.removeItem(USER_SESSION_HINT_KEY);
  } catch (_) {}
}

function clearAdminSessionHint() {
  try {
    localStorage.removeItem(ADMIN_SESSION_HINT_KEY);
  } catch (_) {}
}

/** 永久性会员类型标志：区分会员/游客，可用于全站（如会员服务入口、登录页默认 Tab） */
function setMemberCookie() {
  try {
    const maxAge = MEMBER_COOKIE_MAX_AGE_YEARS * 365 * 24 * 60 * 60;
    document.cookie = `${MEMBER_COOKIE_NAME}=1; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch (_) {}
}

function clearMemberCookie() {
  try {
    document.cookie = `${MEMBER_COOKIE_NAME}=; path=/; max-age=0`;
  } catch (_) {}
}

export function isMemberFromCookie() {
  try {
    return document.cookie.split(';').some((s) => s.trim().startsWith(`${MEMBER_COOKIE_NAME}=`));
  } catch (_) {
    return false;
  }
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [memberFlag, setMemberFlag] = useState(() => isMemberFromCookie());

  // Check login status on mount
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const res = await axios.get('/api/user/me');
      if (res.data.authenticated) {
        setUser(res.data.user);
        setUserSessionHint(true);
        setMemberCookie();
        setMemberFlag(true);
        clearAdminSessionHint();
        try {
          window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
            detail: { isAdmin: false, isLoggedIn: true } 
          }));
        } catch (_) {}
      } else {
        setUser(null);
        setUserSessionHint(false);
        // 不清除会员标志：未登录仅表示登录状态改变，不影响「是否曾为会员」
        try {
          window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
            detail: { isAdmin: false, isLoggedIn: false } 
          }));
        } catch (_) {}
      }
    } catch (e) {
      console.error('Check user error:', e);
      setUser(null);
      setUserSessionHint(false);
      // 不清除会员标志
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/user/login', { email, password });
    if (res.data.success) {
      setUser(res.data.user);
      setUserSessionHint(true);
      setMemberCookie();
      setMemberFlag(true);
      clearAdminSessionHint();
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          detail: { isAdmin: false, isLoggedIn: true }
        }));
      } catch (_) {}
      return true;
    }
    return false;
  };

  const sendEmailVerificationCode = async (email) => {
    const res = await axios.post(
      '/api/auth/send-email-verification',
      { email },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const register = async (username, email, password, email_code) => {
    const res = await axios.post('/api/auth/register', { username, email, password, email_code });
    return res.data.success;
  };

  const forgotPassword = async (email) => {
    const res = await axios.post(
      '/api/auth/forgot-password',
      { email },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const verifyResetCode = async (email, email_code) => {
    const res = await axios.post(
      '/api/auth/verify-password-reset-code',
      { email, email_code },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const resetPassword = async (email, email_code, newPassword) => {
    const res = await axios.post(
      '/api/auth/reset-password',
      { email, email_code, newPassword },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/user/logout');
    } catch (e) {
      console.error('Logout error', e);
    } finally {
      setUser(null);
      setUserSessionHint(false);
      // 不清除会员 cookie / memberFlag：登出后仍是会员，仅登录状态改变
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          detail: { isAdmin: false, isLoggedIn: false } 
        }));
      } catch (_) {}
    }
  };

  const isMember = !!user || memberFlag;

  return (
    <UserContext.Provider value={{ user, loading, isMember, login, register, sendEmailVerificationCode, forgotPassword, verifyResetCode, resetPassword, logout, checkUser }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
